import React from 'react';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { 
  Type, Sun, Zap, Volume2, AlignLeft, MousePointer2, 
  Link2, RotateCcw, Settings, Eye
} from 'lucide-react';
import { Button } from './button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './sheet';

export const AccessibilityPanel = () => {
  const { settings, updateSetting, resetSettings } = useAccessibility();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="icon"
          className="fixed bottom-20 left-4 z-40 rounded-full w-12 h-12 shadow-lg bg-white border-2 border-gray-200 hover:border-[#0047FF]"
          aria-label="Opciones de accesibilidad"
          data-testid="accessibility-toggle"
        >
          <Eye className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Accesibilidad
          </SheetTitle>
          <SheetDescription>
            Personaliza la visualización para mejorar tu experiencia
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Font Size */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-3">
              <Type className="w-4 h-4" />
              Tamaño de texto
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'small', label: 'S' },
                { value: 'normal', label: 'M' },
                { value: 'large', label: 'L' },
                { value: 'xlarge', label: 'XL' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateSetting('fontSize', option.value)}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    settings.fontSize === option.value
                      ? 'bg-[#0047FF] text-white border-[#0047FF]'
                      : 'bg-white border-gray-200 hover:border-[#0047FF]'
                  }`}
                  aria-pressed={settings.fontSize === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* High Contrast */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Sun className="w-4 h-4" />
              Alto contraste
            </label>
            <button
              onClick={() => updateSetting('highContrast', !settings.highContrast)}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.highContrast ? 'bg-[#0047FF]' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.highContrast}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.highContrast ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Reduce Motion */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Zap className="w-4 h-4" />
              Reducir animaciones
            </label>
            <button
              onClick={() => updateSetting('reduceMotion', !settings.reduceMotion)}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.reduceMotion ? 'bg-[#0047FF]' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.reduceMotion}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.reduceMotion ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Screen Reader Mode */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Volume2 className="w-4 h-4" />
              Modo lector pantalla
            </label>
            <button
              onClick={() => updateSetting('screenReader', !settings.screenReader)}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.screenReader ? 'bg-[#0047FF]' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.screenReader}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.screenReader ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Line Spacing */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-3">
              <AlignLeft className="w-4 h-4" />
              Espaciado de líneas
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'normal', label: 'Normal' },
                { value: 'relaxed', label: 'Amplio' },
                { value: 'loose', label: 'Muy amplio' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateSetting('lineSpacing', option.value)}
                  className={`py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${
                    settings.lineSpacing === option.value
                      ? 'bg-[#0047FF] text-white border-[#0047FF]'
                      : 'bg-white border-gray-200 hover:border-[#0047FF]'
                  }`}
                  aria-pressed={settings.lineSpacing === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cursor Size */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium">
              <MousePointer2 className="w-4 h-4" />
              Cursor grande
            </label>
            <button
              onClick={() => updateSetting('cursorSize', settings.cursorSize === 'normal' ? 'large' : 'normal')}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.cursorSize === 'large' ? 'bg-[#0047FF]' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.cursorSize === 'large'}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.cursorSize === 'large' ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Link Highlight */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Link2 className="w-4 h-4" />
              Resaltar enlaces
            </label>
            <button
              onClick={() => updateSetting('linkHighlight', !settings.linkHighlight)}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.linkHighlight ? 'bg-[#0047FF]' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.linkHighlight}
            >
              <span 
                className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.linkHighlight ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {/* Reset Button */}
          <Button
            onClick={resetSettings}
            variant="outline"
            className="w-full mt-4"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restablecer opciones
          </Button>

          {/* Info */}
          <p className="text-xs text-gray-500 mt-4">
            Tus preferencias se guardan automáticamente en este dispositivo.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AccessibilityPanel;
